import type { Scenario } from "@radar-sim/schema";

/**
 * Scaffolding helpers for the authoring UI. Each returns a snippet that can
 * be slotted into a `Scenario` to give the author a working template
 * (rather than starting from a blank object).
 */

export const SCAFFOLD_SCENARIO: Scenario = {
  schemaVersion: 1,
  metadata: {
    id: "new-scenario",
    name: "New scenario",
    description: "Authored scenario.",
    syllabusTags: ["g34.familiarisation.range-scales"],
    area: "solent",
    difficulty: 1,
    expectedDurationMinutes: 15,
    author: "you",
    scenarioVersion: "0.1.0",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  initialConditions: {
    ownShip: {
      position: { lat: 50.74, lon: -1.4 },
      cog: 90,
      sog: 6,
      heading: 90,
    },
    simStartWallClock: "2026-06-15T10:00:00+01:00",
    environment: {
      seaState: 2,
      windFromDeg: 230,
      windSpeedKnots: 12,
      visibilityNm: 8,
      rainCells: [],
      tidePhase: "mid",
    },
  },
  vessels: [],
  events: [],
  successCriteria: [{ kind: "complete_within", seconds: 900 }],
};

export const SCAFFOLD_TRACKED_VESSEL = {
  kind: "tracked",
  id: "new-vessel",
  vesselType: "yacht-sail",
  rcs: 0.2,
  waypoints: [
    { atSimSeconds: 0, position: { lat: 50.74, lon: -1.34 }, cog: 90, sog: 5 },
    { atSimSeconds: 600, position: { lat: 50.74, lon: -1.25 }, cog: 90, sog: 5 },
  ],
};

export const SCAFFOLD_BEHAVIOURAL_VESSELS: Record<string, unknown> = {
  follow_lane: {
    kind: "behavioural",
    id: "follow-lane-vessel",
    vesselType: "ferry",
    rcs: 0.9,
    spawn: { position: { lat: 50.71, lon: -1.55 }, cog: 90, sog: 9 },
    behaviour: { name: "follow_lane", laneId: "western-solent-eastbound" },
  },
  random_walk: {
    kind: "behavioural",
    id: "random-walk-vessel",
    vesselType: "fishing",
    rcs: 0.25,
    spawn: { position: { lat: 50.72, lon: -1.18 }, cog: 0, sog: 4 },
    behaviour: {
      name: "random_walk",
      bounds: {
        nw: { lat: 50.78, lon: -1.25 },
        se: { lat: 50.66, lon: -1.1 },
      },
      speedRangeKnots: [3, 6],
    },
  },
  fishing_pattern: {
    kind: "behavioural",
    id: "fishing-pattern-vessel",
    vesselType: "fishing",
    rcs: 0.25,
    spawn: { position: { lat: 50.79, lon: -1.295 }, cog: 0, sog: 3 },
    behaviour: {
      name: "fishing_pattern",
      centre: { lat: 50.79, lon: -1.295 },
      radiusNm: 0.3,
      speedKnots: 3,
    },
  },
  colregs_giveway: {
    kind: "behavioural",
    id: "colregs-giveway-vessel",
    vesselType: "yacht-sail",
    rcs: 0.2,
    spawn: { position: { lat: 50.74, lon: -1.34 }, cog: 0, sog: 5 },
    behaviour: { name: "colregs_giveway", targetVesselId: "your-own-id-here" },
  },
};

export const SCAFFOLD_EVENTS: Record<string, unknown> = {
  rain_cell_appears: {
    atSimSeconds: 300,
    event: {
      kind: "rain_cell_appears",
      cell: {
        id: "cell-1",
        centre: { lat: 50.74, lon: -1.36 },
        radiusNm: 0.4,
        intensity: 3,
        driftCog: 90,
        driftSpeedKnots: 8,
      },
    },
  },
  visibility_changes: {
    atSimSeconds: 300,
    event: {
      kind: "visibility_changes",
      toNm: 1,
      transitionSeconds: 120,
    },
  },
  sea_state_changes: {
    atSimSeconds: 600,
    event: {
      kind: "sea_state_changes",
      toState: 4,
      transitionSeconds: 240,
    },
  },
  mob_drop: {
    atSimSeconds: 60,
    event: {
      kind: "mob_drop",
      relativeToOwn: { asternNm: 0.05, abeamNm: 0.02, abeamSide: "port" },
    },
  },
  racon_activates: {
    atSimSeconds: 120,
    event: {
      kind: "racon_activates",
      raconId: "nab-tower",
      durationSeconds: 30,
    },
  },
  equipment_failure: {
    atSimSeconds: 300,
    event: {
      kind: "equipment_failure",
      system: "heading-sensor",
      mode: "disagree",
      durationSeconds: 120,
    },
  },
};

export const SCAFFOLD_SUCCESS_CRITERIA: Record<string, unknown> = {
  acquire_target: { kind: "acquire_target", vesselId: "vessel-id", withinSeconds: 240 },
  maintain_cpa_above: { kind: "maintain_cpa_above", nm: 0.3 },
  complete_within: { kind: "complete_within", seconds: 1200 },
  recover_mob: { kind: "recover_mob", withinSeconds: 360, toleranceNm: 0.05 },
  identify_target: { kind: "identify_target", vesselId: "vessel-id", correctTypeRequired: true },
};
